import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { activities } from "@/data/activities";
import { ACTIVITIES } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

const cardTestId = {
  barrancos: ACTIVITIES.cardBarrancos,
  cuevas: ACTIVITIES.cardCuevas,
  ferratas: ACTIVITIES.cardFerratas,
  escalada: "activity-card-escalada",
};

// Reorder cards for display: hero card first, then 3 in a row
const DISPLAY_ORDER = ["barrancos", "cuevas", "ferratas", "escalada"];

export default function ActivitiesBento() {
  const { t, lang } = useLang();
  const ordered = DISPLAY_ORDER
    .map((s) => activities.find((a) => a.slug === s))
    .filter(Boolean);
  const [hero, ...rest] = ordered;

  return (
    <section
      id="actividades"
      data-testid={ACTIVITIES.section}
      className="bg-[#EAE7E0] py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#F26419] mb-4">
              {t("activities.kicker")}
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tighter text-[#1A1B1A] max-w-3xl">
              {t("activities.title")}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <ActivityCard
            activity={hero}
            t={t}
            lang={lang}
            className="md:col-span-12 min-h-[380px] md:min-h-[480px]"
            big
          />
          {rest.map((a) => (
            <ActivityCard
              key={a.slug}
              activity={a}
              t={t}
              lang={lang}
              className="md:col-span-4 min-h-[300px]"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ActivityCard({ activity, t, lang, className = "", big = false }) {
  return (
    <Link
      to={`/actividades/${activity.slug}`}
      data-testid={cardTestId[activity.slug]}
      className={`group relative block overflow-hidden border-2 border-[#1A1B1A] bg-[#1A1B1A] ${className}`}
    >
      <img
        src={activity.image}
        alt={activity.title[lang]}
        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-105 transition-all duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      <div className="relative h-full w-full p-6 md:p-8 flex flex-col justify-between text-white">
        <div className="flex items-start justify-between">
          <span className="text-xs tracking-[0.3em] uppercase font-bold bg-[#F26419] px-3 py-1.5">
            {activity.difficulty[lang]}
          </span>
          <ArrowUpRight className="w-6 h-6 text-white group-hover:text-[#F26419] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
        </div>

        <div>
          <h3 className={`font-display font-black tracking-tighter leading-[0.95] ${big ? "text-5xl sm:text-6xl md:text-7xl" : "text-3xl md:text-4xl"}`}>
            {activity.title[lang]}
          </h3>
          <p className={`mt-3 text-white/85 ${big ? "text-base md:text-lg max-w-xl" : "text-sm"}`}>
            {activity.tagline[lang]}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs tracking-wider uppercase font-semibold text-white/80">
            <span>{activity.duration[lang]}</span>
            <span className="w-1 h-1 bg-[#F26419] rounded-full" />
            <span>{lang === "es" ? activity.price : activity.priceEn}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
